import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { RefImagePicker, fileToRef, type RefImage } from './RefImagePicker';

/**
 * Image studio — the image generator's OWN TAB (Evan 2026-10-28: "give image generator its own
 * home…"), with the same multi-project draft management as Make anything (Evan, later the same
 * day: "need the same version/draft management as make anything page with the panel on the
 * right where i can press new").
 *
 * PROJECTS panel on the right: each project is an independent image with its own prompt, refs
 * and v-numbered refinement chain; + New starts a fresh one; everything persists per-browser.
 * "Use in asset" feeds a draft into Make anything's allowed-image set (shared across projects).
 *
 * Store: 'shado-admin:image-studio:v2' (projects). The flat v1 key migrates into a single
 * project on first load. Make anything reads the shared `backdrops` via loadImageGen().
 */

export type ImgDraft = {
  url: string;
  prompt: string;
  kind?: 'backdrop' | 'image';
  aspect?: string;
  /** What the art-director pass actually sent to Gemini (studio-image `directed`). */
  directed?: string;
};

type ImgProject = {
  label?: string;
  prompt?: string;
  refs?: RefImage[];
  drafts?: ImgDraft[];
  idx?: number;
  at?: number;
};
type StoreV2 = {
  current: string;
  order: string[];
  projects: Record<string, ImgProject>;
  /** Images shared into Make anything's allowed set ("Use in asset"). */
  backdrops?: ImgDraft[];
};

const IMAGE_GEN_LS_V1 = 'shado-admin:studio-backdrops:v1';
const IMAGE_GEN_LS = 'shado-admin:image-studio:v2';

function loadStore(): StoreV2 {
  try {
    const raw = localStorage.getItem(IMAGE_GEN_LS);
    if (raw) {
      const st = JSON.parse(raw) as StoreV2;
      if (st && st.projects && Array.isArray(st.order) && st.order.length > 0) return st;
    }
  } catch {
    /* fall through to migration */
  }
  // Migrate the flat v1 slot (pre-projects) into a single project once.
  const id = crypto.randomUUID();
  const st: StoreV2 = { current: id, order: [id], projects: { [id]: {} }, backdrops: [] };
  try {
    const raw = localStorage.getItem(IMAGE_GEN_LS_V1);
    if (raw) {
      const v1 = JSON.parse(raw) as { backdrops?: ImgDraft[]; prompt?: string; refs?: RefImage[]; drafts?: ImgDraft[] };
      st.projects[id] = { prompt: v1.prompt, refs: v1.refs, drafts: v1.drafts, at: Date.now() };
      st.backdrops = v1.backdrops ?? [];
    }
  } catch {
    /* fresh store */
  }
  return st;
}

function saveStore(st: StoreV2) {
  try {
    localStorage.setItem(IMAGE_GEN_LS, JSON.stringify(st));
  } catch {
    try {
      // Refs are the heavy part — drop them everywhere if storage is full.
      const slim: StoreV2 = {
        ...st,
        projects: Object.fromEntries(Object.entries(st.projects).map(([k, p]) => [k, { ...p, refs: undefined }])),
      };
      localStorage.setItem(IMAGE_GEN_LS, JSON.stringify(slim));
    } catch {
      /* storage unavailable */
    }
  }
}

/** Make anything's read of the shared allowed-image set. */
export function loadImageGen(): { backdrops?: ImgDraft[] } {
  return { backdrops: loadStore().backdrops ?? [] };
}

function labelOf(p: ImgProject | undefined): string {
  if (!p) return 'untitled';
  if (p.label && p.label.trim()) return p.label.trim();
  const first = p.drafts?.[0]?.prompt ?? p.prompt ?? '';
  return first.trim().slice(0, 48) || 'untitled';
}

/** Fetch a generated image back as a ≤1024px JPEG inline payload so a refine can send it to
 * Gemini as the image being edited. The cache-buster forces a CORS fetch — the preview <img>
 * loads the same URL without crossOrigin and Chrome may otherwise reuse that response, tainting
 * the canvas. */
async function urlToInline(url: string): Promise<{ mime: string; data: string }> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Could not load the image to refine — regenerate it first'));
    i.src = `${url}${url.includes('?') ? '&' : '?'}cors=1`;
  });
  const MAX = 1024;
  const s = Math.min(1, MAX / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * s));
  canvas.height = Math.max(1, Math.round(img.height * s));
  canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
  const jpeg = canvas.toDataURL('image/jpeg', 0.9);
  return { mime: 'image/jpeg', data: jpeg.slice(jpeg.indexOf(',') + 1) };
}

const ASPECTS = ['1:1', '3:4', '4:3', '9:16', '16:9'] as const;

/** Pull the function's own error message out of a non-2xx invoke, map the Gemini 402. */
async function invokeErrorMessage(fnErr: Error, fallback: string): Promise<string> {
  let msg = fnErr.message || fallback;
  const ctx = (fnErr as { context?: Response }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const parsed = (await ctx.json()) as { error?: string };
      if (parsed?.error) msg = parsed.error;
    } catch {
      /* keep default */
    }
  }
  if (/prepayment credits|RESOURCE_EXHAUSTED|402/i.test(msg)) {
    msg = 'Gemini is out of prepaid credits — add credits at ai.studio/projects, then retry.';
  }
  return msg;
}

export function ImageStudio() {
  const [boot] = useState(loadStore);
  const [currentId, setCurrentId] = useState(boot.current);
  const [tabs, setTabs] = useState<{ id: string; label: string }[]>(
    boot.order.map((id) => ({ id, label: labelOf(boot.projects[id]) })),
  );
  const cur0 = boot.projects[boot.current] ?? {};
  const [prompt, setPrompt] = useState(cur0.prompt ?? '');
  const [refs, setRefs] = useState<RefImage[]>(cur0.refs ?? []);
  const [drafts, setDrafts] = useState<ImgDraft[]>(cur0.drafts ?? []);
  const [idx, setIdx] = useState(Math.min(cur0.idx ?? Math.max(0, (cur0.drafts?.length ?? 1) - 1), Math.max(0, (cur0.drafts?.length ?? 1) - 1)));
  const [projLabel, setProjLabel] = useState(cur0.label ?? '');
  const [inAsset, setInAsset] = useState<ImgDraft[]>(boot.backdrops ?? []);
  const [aspect, setAspect] = useState<(typeof ASPECTS)[number]>('1:1');
  const [refine, setRefine] = useState('');
  const [busy, setBusy] = useState<null | 'generate' | 'refine'>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [usage, setUsage] = useState<{ month: number; cap: number } | null>(null);

  // ---- autosave the current project into its store slot ----
  useEffect(() => {
    const store = loadStore();
    store.current = currentId;
    if (!store.order.includes(currentId)) store.order.push(currentId);
    store.projects[currentId] = {
      label: projLabel.trim() || undefined,
      prompt,
      refs,
      drafts,
      idx,
      at: Date.now(),
    };
    // Cap the slots; oldest non-current goes first.
    while (store.order.length > 12) {
      const drop = store.order.find((id) => id !== currentId) ?? store.order[0];
      store.order = store.order.filter((x) => x !== drop);
      delete store.projects[drop];
    }
    store.backdrops = inAsset;
    saveStore(store);
    setTabs(store.order.map((id) => ({ id, label: labelOf(store.projects[id]) })));
  }, [prompt, refs, drafts, idx, projLabel, inAsset, currentId]);

  /** Load a project slot into working state (null = fresh empty project). */
  const applyProject = useCallback((p: ImgProject | null) => {
    setPrompt(p?.prompt ?? '');
    setRefs(p?.refs ?? []);
    const ds = p?.drafts ?? [];
    setDrafts(ds);
    setIdx(Math.min(p?.idx ?? Math.max(0, ds.length - 1), Math.max(0, ds.length - 1)));
    setProjLabel(p?.label ?? '');
    setRefine('');
    setError(null);
    setNotice(null);
  }, []);

  const switchProject = useCallback(
    (id: string) => {
      if (id === currentId) return;
      const store = loadStore();
      // Drop never-used empty projects the user is leaving behind.
      store.order = store.order.filter((x) => {
        if (x === id || x === currentId) return true;
        const p = store.projects[x];
        const empty = !p || ((p.drafts?.length ?? 0) === 0 && !(p.prompt ?? '').trim() && (p.refs?.length ?? 0) === 0);
        if (empty) delete store.projects[x];
        return !empty;
      });
      store.current = id;
      saveStore(store);
      setCurrentId(id);
      applyProject(store.projects[id] ?? null);
      setTabs(store.order.map((x) => ({ id: x, label: labelOf(store.projects[x]) })));
    },
    [currentId, applyProject],
  );

  const newProject = useCallback(() => {
    const id = crypto.randomUUID();
    setCurrentId(id);
    applyProject(null);
    setTabs((ts) => [...ts, { id, label: 'untitled' }]);
  }, [applyProject]);

  const renameProject = useCallback(
    (id: string) => {
      const store = loadStore();
      const next = window.prompt('Name this image project:', labelOf(store.projects[id]));
      if (next == null) return;
      if (id === currentId) {
        setProjLabel(next);
        return;
      }
      if (store.projects[id]) {
        store.projects[id].label = next.trim() || undefined;
        saveStore(store);
        setTabs(store.order.map((x) => ({ id: x, label: labelOf(store.projects[x]) })));
      }
    },
    [currentId],
  );

  const deleteProject = useCallback(
    (id: string) => {
      const store = loadStore();
      const name = labelOf(store.projects[id]);
      if (!window.confirm(`Delete image project “${name}” and its drafts? Images already used in assets stay available.`)) return;
      store.order = store.order.filter((x) => x !== id);
      delete store.projects[id];
      if (store.order.length === 0) {
        const fresh = crypto.randomUUID();
        store.order = [fresh];
        store.projects[fresh] = {};
        store.current = fresh;
      } else if (store.current === id) {
        store.current = store.order[store.order.length - 1];
      }
      saveStore(store);
      setTabs(store.order.map((x) => ({ id: x, label: labelOf(store.projects[x]) })));
      if (id === currentId) {
        setCurrentId(store.current);
        applyProject(store.projects[store.current] ?? null);
      }
    },
    [currentId, applyProject],
  );

  /** Drop or paste images ANYWHERE on this tab to add refs. */
  const [dragOver, setDragOver] = useState(0);
  const addRefFiles = useCallback(
    async (files: File[]) => {
      const imgs = files.filter((f) => f.type.startsWith('image/'));
      if (imgs.length === 0) return;
      const room = 6 - refs.length;
      if (room <= 0) {
        setNotice('Reference slots are full (6) — remove one first.');
        return;
      }
      try {
        const added = await Promise.all(imgs.slice(0, room).map(fileToRef));
        setRefs([...refs, ...added]);
        setNotice(null);
      } catch {
        setError('Could not read that image — try a JPG or PNG.');
      }
    },
    [refs],
  );
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => Boolean(f));
      if (files.length > 0) {
        e.preventDefault();
        void addRefFiles(files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [addRefFiles]);

  const cur = drafts[idx];

  const pushDraft = useCallback((entry: ImgDraft) => {
    setDrafts((ds) => {
      const next = [...ds.filter((x) => x.url !== entry.url), entry].slice(-20);
      setIdx(next.length - 1);
      return next;
    });
  }, []);

  const applyUsage = useCallback((u?: { month_cost_usd?: number; monthly_cap_usd?: number }) => {
    if (u && typeof u.month_cost_usd === 'number') {
      setUsage({ month: u.month_cost_usd, cap: u.monthly_cap_usd ?? 10 });
    }
  }, []);

  const generate = useCallback(
    async (mode: 'image' | 'backdrop') => {
      const p = prompt.trim();
      if (!p || busy) return;
      setBusy('generate');
      setError(null);
      setNotice(null);
      const { data, error: fnErr } = await supabase.functions.invoke('studio-image', {
        body: { prompt: p, aspect, mode, refs: refs.map((r) => ({ mime: r.mime, data: r.data })) },
      });
      setBusy(null);
      if (fnErr) {
        setError(await invokeErrorMessage(fnErr, 'Image generation failed'));
        return;
      }
      const d = (data ?? {}) as {
        url?: string;
        directed?: string;
        usage?: { month_cost_usd?: number; monthly_cap_usd?: number };
      };
      if (!d.url) {
        setError('Generation returned nothing — rephrase and retry');
        return;
      }
      const entry: ImgDraft = { url: d.url, prompt: p, kind: mode, aspect, directed: d.directed };
      pushDraft(entry);
      if (mode === 'backdrop') {
        setInAsset((bs) => [...bs.filter((x) => x.url !== entry.url).slice(-5), entry]);
      }
      applyUsage(d.usage);
      setPrompt('');
    },
    [prompt, busy, aspect, refs, pushDraft, applyUsage],
  );

  const refineDraft = useCallback(async () => {
    const instr = refine.trim();
    if (!cur || !instr || busy) return;
    setBusy('refine');
    setError(null);
    setNotice(null);
    try {
      const base = await urlToInline(cur.url);
      const { data, error: fnErr } = await supabase.functions.invoke('studio-image', {
        body: {
          prompt: instr,
          aspect: cur.aspect ?? aspect,
          mode: 'image',
          base,
          refs: refs.map((r) => ({ mime: r.mime, data: r.data })),
          // The chain up to the version being refined — the art director restates it so
          // earlier directives survive later steps.
          history: drafts.slice(0, idx + 1).map((d2) => d2.prompt),
        },
      });
      if (fnErr) {
        setError(await invokeErrorMessage(fnErr, 'Refine failed'));
        return;
      }
      const d = (data ?? {}) as {
        url?: string;
        directed?: string;
        usage?: { month_cost_usd?: number; monthly_cap_usd?: number };
      };
      if (!d.url) {
        setError('Refine returned nothing — rephrase and retry');
        return;
      }
      pushDraft({ url: d.url, prompt: instr, kind: cur.kind, aspect: cur.aspect ?? aspect, directed: d.directed });
      applyUsage(d.usage);
      setRefine('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refine failed');
    } finally {
      setBusy(null);
    }
  }, [cur, refine, busy, aspect, refs, drafts, idx, pushDraft, applyUsage]);

  const curInAsset = Boolean(cur && inAsset.some((b) => b.url === cur.url));

  return (
    <div
      className="relative"
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes('Files')) setDragOver((n) => n + 1);
      }}
      onDragLeave={() => setDragOver((n) => Math.max(0, n - 1))}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(0);
        void addRefFiles(Array.from(e.dataTransfer.files));
      }}
    >
      {dragOver > 0 ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-accent bg-accent/10">
          <span className="rounded-lg bg-black/70 px-4 py-2 text-[13px] font-medium text-white">
            Drop to add as reference image{refs.length >= 5 ? '' : 's'} ({refs.length}/6 used)
          </span>
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[380px,minmax(0,1fr),190px]">
        {/* -------- controls column -------- */}
        <div className="min-w-0 space-y-4">
          <div className="admin-card">
            <div className="admin-eyebrow mb-2">Describe the image — Gemini 3 Pro, ~13¢</div>
            <textarea
              className="admin-input !h-auto !py-3 resize-y"
              rows={3}
              placeholder="e.g. abstract first-floor nightclub interior, purple accent lighting, cinematic, darkest at the bottom"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void generate('image');
                }
              }}
            />
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-mutedDim">Aspect</span>
              {ASPECTS.map((a) => (
                <button key={a} type="button" data-active={aspect === a} onClick={() => setAspect(a)} className="admin-pill">
                  {a}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                disabled={busy != null || !prompt.trim()}
                onClick={() => void generate('image')}
                className="admin-btn admin-btn-primary admin-btn-sm"
                title="Freeform image — exactly what you describe"
              >
                {busy === 'generate' ? 'Generating…' : 'Generate'}
              </button>
              <button
                type="button"
                disabled={busy != null || !prompt.trim()}
                onClick={() => void generate('backdrop')}
                className="admin-btn admin-btn-secondary admin-btn-sm"
                title="Abstract dark-premium texture in the Shado palette — lands straight in Make anything's allowed images"
              >
                Backdrop
              </button>
            </div>
            <div className="mt-3">
              <RefImagePicker refs={refs} setRefs={setRefs} disabled={busy != null} />
            </div>
            <p className="mt-2 text-[10.5px] leading-relaxed text-mutedDim">
              References guide style, mood and palette — add or swap them between steps; they apply
              to generates and refines alike. Drop images anywhere on this tab, or paste a
              screenshot (Cmd-V), to add them.
              {usage ? ` This month: $${usage.month.toFixed(2)} of $${usage.cap} cap.` : ''}
            </p>
          </div>

          {drafts.length > 0 ? (
            <div className="admin-card">
              <div className="admin-eyebrow mb-2">Drafts — every step is kept</div>
              <div className="max-h-[420px] space-y-1 overflow-y-auto">
                {drafts.map((d, i) => (
                  <button
                    key={d.url}
                    type="button"
                    onClick={() => setIdx(i)}
                    className={`flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-white/[0.03] ${
                      i === idx ? 'bg-accent/10' : ''
                    }`}
                    title={d.directed ? `${d.prompt}\n\nArt director sent:\n${d.directed}` : d.prompt}
                  >
                    <img src={d.url} alt="" className="h-10 w-10 shrink-0 rounded object-cover" loading="lazy" />
                    <span className="shrink-0 text-[11px] font-semibold tabular-nums text-[#b79cff]">v{i + 1}</span>
                    <span className={`min-w-0 flex-1 truncate text-[11.5px] ${i === idx ? 'text-textPrimary' : 'text-muted'}`}>
                      {d.prompt}
                    </span>
                    {inAsset.some((b) => b.url === d.url) ? (
                      <span className="shrink-0 text-[9.5px] uppercase tracking-[0.1em] text-emerald-400">asset</span>
                    ) : null}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[10.5px] leading-relaxed text-mutedDim">
                Tap a version to bring it back — the refine box works on whichever version is
                selected, so you can branch from any step. Drafts survive refreshes.
              </p>
            </div>
          ) : null}
        </div>

        {/* -------- preview column -------- */}
        <div className="min-w-0">
          {error ? <div className="admin-banner-error mb-3">{error}</div> : null}
          {notice ? (
            <div className="mb-3 rounded-lg border border-borderStrong bg-surfaceElevated px-3 py-2 text-[12px] text-muted">
              {notice}
            </div>
          ) : null}
          {cur ? (
            <div className="admin-card">
              {/* Follow-up ABOVE the image — under a 72vh preview it fell below the fold and
                  read as "refining is gone" (Evan 2026-10-28). */}
              <div className="mb-3 flex items-center gap-2">
                <input
                  className="admin-input flex-1 !text-[12.5px]"
                  placeholder={`Follow up on v${idx + 1} — e.g. “darker, more neon, remove the people”`}
                  value={refine}
                  onChange={(e) => setRefine(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void refineDraft();
                  }}
                />
                <button
                  type="button"
                  disabled={busy != null || !refine.trim()}
                  onClick={() => void refineDraft()}
                  className="admin-btn admin-btn-primary admin-btn-sm whitespace-nowrap"
                  title="Refines the version selected in Drafts — the result lands as a new version"
                >
                  {busy === 'refine' ? 'Working…' : drafts.length > 1 ? `Refine v${idx + 1}` : 'Refine'}
                </button>
              </div>
              <a href={cur.url} target="_blank" rel="noreferrer" title="Open full size in a new tab">
                <img
                  src={cur.url}
                  alt={cur.prompt}
                  className={`mx-auto max-h-[66vh] w-auto max-w-full rounded-lg border border-border ${
                    busy ? 'opacity-60' : ''
                  }`}
                />
              </a>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                {curInAsset ? (
                  <button
                    type="button"
                    onClick={() => setInAsset((bs) => bs.filter((x) => x.url !== cur.url))}
                    className="admin-btn admin-btn-ghost admin-btn-sm"
                    title="Make anything will stop seeing this image"
                  >
                    In asset ✓ — remove
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setInAsset((bs) => [...bs.filter((x) => x.url !== cur.url).slice(-5), cur]);
                      setNotice('Added to Make anything’s allowed images — mention it in a brief or refine there.');
                    }}
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    title="Let Make anything place this image in designs"
                  >
                    Use in asset
                  </button>
                )}
                <a href={cur.url} download className="admin-btn admin-btn-ghost admin-btn-sm" title="Download the PNG">
                  Download
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setInAsset((bs) => bs.filter((x) => x.url !== cur.url));
                    setDrafts((ds) => {
                      const next = ds.filter((x) => x.url !== cur.url);
                      setIdx((i) => Math.max(0, Math.min(i, next.length - 1)));
                      return next;
                    });
                  }}
                  className="admin-btn admin-btn-ghost admin-btn-sm"
                >
                  Delete draft
                </button>
                <span className="text-[10.5px] text-mutedDim">
                  v{idx + 1} of {drafts.length} · {cur.aspect ?? '1:1'}
                </span>
              </div>
            </div>
          ) : (
            <div className="admin-card">
              <p className="text-[12.5px] leading-relaxed text-mutedDim">
                Describe an image on the left and hit Generate. The result lands here big; follow
                up in plain words to refine it, and every step is kept as a draft you can come
                back to — including after a refresh. &ldquo;Use in asset&rdquo; hands the image to
                Make anything; Backdrop-mode textures go there automatically. Separate ideas go
                in separate projects — &ldquo;+ New&rdquo; on the right.
              </p>
            </div>
          )}
        </div>

        {/* -------- projects panel (same pattern as Make anything's drafts) -------- */}
        <div className="min-w-0">
          <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-mutedDim">Projects</div>
          <div className="space-y-1.5">
            {tabs.map((t) => (
              <div
                key={t.id}
                className={`flex items-center gap-1 rounded-lg border px-2 py-1.5 text-[11.5px] ${
                  t.id === currentId ? 'border-accent/70 bg-accent/10' : 'border-border bg-surface'
                }`}
              >
                <button
                  type="button"
                  onClick={() => switchProject(t.id)}
                  className={`min-w-0 flex-1 truncate text-left ${
                    t.id === currentId ? 'text-white' : 'text-muted hover:text-white'
                  }`}
                  title={t.id === currentId ? 'Current project' : 'Switch to this project'}
                >
                  {t.label}
                </button>
                <button
                  type="button"
                  onClick={() => renameProject(t.id)}
                  className="shrink-0 text-[10px] text-mutedDim hover:text-white"
                  aria-label={`Rename ${t.label}`}
                  title="Rename"
                >
                  ✎
                </button>
                <button
                  type="button"
                  onClick={() => deleteProject(t.id)}
                  className="shrink-0 text-[10px] text-mutedDim hover:text-danger"
                  aria-label={`Delete ${t.label}`}
                  title="Delete project"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={newProject}
              className="w-full rounded-lg border border-dashed border-border px-2 py-1.5 text-[11.5px] text-mutedDim transition-colors hover:border-accent/50 hover:text-white"
            >
              + New
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
